import dynamic from "next/dynamic";
import Head from "next/head";
import React, { useState, useEffect } from "react";
const Footer = dynamic(() => import("../../../components/Footer"), { ssr: false });
const Navbar = dynamic(() => import("../../../components/Navbar"), { ssr: false });
const GetInTouch = dynamic(() => import("../../../components/reusable/GetInTouch"), { ssr: false });
// import blogData from "../../blogs.json";
import whitepaperData from "../../../whitepapers.json";
import ContactUsHome from "../../../components/homepage/ContactUsHome";
import Image from "next/future/image";
import { MetaMetricRenderer } from "../../../components/blog/MetaMetricRenderer";
import Link from "next/link";

export async function getStaticPaths() {
  //   let blogPaths = blogData.filter(i => i?.meta?.slug && typeof i.meta.slug === "string").map(i => ({ params: { slug: i.meta.slug } }));

  let whitepaperPaths = whitepaperData.filter(i => i?.meta?.slug && typeof i.meta.slug === "string").map(i => ({ params: { slug: i.meta.slug } }));
  return {
    paths: whitepaperPaths,
    fallback: false,
  };
}

export async function getStaticProps(context) {
  let slug = context.params.slug;
  const data = whitepaperData.find(item => item.meta.slug === slug) || whitepaperData.find(item => item.meta.slug === slug);

  if (!data) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      content: data.content,
      metadata: data.meta,
      noAutoHeader: "no",
    },
  };
}

export default function WhitePaperPage({ content, metadata }) {
  const [showButton, setShowButton] = useState(false);
  // const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const imageSection = document.getElementById("image-section");
    const footer = document.getElementById("contact-us");

    if (!imageSection || !footer) return;

    const observer = new IntersectionObserver(
      entries => {
        let isImageVisible = false;
        let isFooterVisible = false;

        entries.forEach(entry => {
          if (entry.target === imageSection) {
            isImageVisible = entry.isIntersecting;
          }
          if (entry.target === footer) {
            isFooterVisible = entry.isIntersecting;
          }
        });

        // Show button when image is **out of view** and footer is **not in view**
        setShowButton(!isImageVisible && !isFooterVisible);
      },
      { threshold: 0.3 },
    );

    observer.observe(imageSection);
    observer.observe(footer);

    return () => observer.disconnect();
  }, []);

  function createMarkup(content, quotes = [], metrics = []) {
    let modifiedContent = content;
    // let quoteIndex = 0;

    quotes.forEach((quote, index) => {
      modifiedContent = modifiedContent.replace(
        `{{quote_${index + 1}}}`,
        `<blockquote class="border-l-4 border-[#f1aa4e] pl-4 italic text-gray-500 font-semibold text-2xl py-5">
              ${quote}
          </blockquote>`,
      );
    });

    // Replace each {{metric_X}} placeholder with an empty div
    metrics.forEach((metric, index) => {
      modifiedContent = modifiedContent.replace(`{{metric_${index + 1}}}`, `<div id="dynamic-metric-${index + 1}"></div>`);
    });

    return { __html: modifiedContent };
  }

  const [fontSize, setFontSize] = useState("text-4xl");
  useEffect(() => {
    const handleResize = () => {
      setFontSize(window.innerWidth < 640 ? "text-3xl" : "text-4xl");
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <div className="bg-white">
      <Head>
        <meta name="description" content={metadata?.description} />
        <meta name="keywords" content={metadata?.keywords} />
        {metadata?.author && <meta name="author" content={metadata?.author} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metadata?.title} />
        <meta property="og:description" content={metadata?.description} />
        <meta property="og:image" content={`${process.env.NEXT_PUBLIC_WEBSITE + metadata?.img}`} />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_WEBSITE + metadata?.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:description" content={metadata?.subtitle} />
        <meta name="twitter:title" content={metadata?.title} />
        <meta name="twitter:image" content={`${process.env.NEXT_PUBLIC_WEBSITE + metadata?.img}`} />
        <title>{metadata?.title}</title>
        <link rel="icon" type="image/svg" href="/favicon.svg" sizes="16x16"></link>
      </Head>

      <Navbar />

      <section className="mb-20">
        <div id="image-section" className="flex flex-col lg:flex-row w-full lg:h-screen justify-start  lg:justify-center mt-14 lg:mt-16 lg:py-0 py-10">
          {/* Text Section */}
          <div className="prose md:prose-xl lg:prose-xl lg:w-1/2 w-full flex flex-col items-start lg:items-center h-full justify-center">
            <div className="lg:px-12 px-6 lg:my-0 my-10 w-full">
              <div className="w-full">
                <span className="text-gray-900 border-b-2 border-[#f1aa4e]">White Paper</span>
                <h1 className={`${fontSize} font-sans font-medium !mb-0 !mt-5`}>{metadata?.title}</h1>
                <h3 className="text-gray-500 font-normal !mt-4">{metadata?.subtitle}</h3>
              </div>
              {/* ShareOptions at the bottom only for large screens */}
              {/* <Link href="/blog/future-proof-your-hospitality-supply-chain" className="md:block hidden">
                <span className="text-lg bg-amber-600 text-white p-2 rounded-lg md:inline-flex gap-2 hover:translate-x-1 transition-all duration-300 cursor-pointer hidden">
                  Read Full White Paper
                </span>
              </Link> */}
              {/* <div className="w-full mt-5">
                <ShareOptions title={metadata?.title} />
              </div> */}
            </div>
          </div>

          {/* Image Section */}
          <div className="lg:w-1/2 w-full flex justify-center">
            {metadata?.img && (
              <Image
                src={metadata.img}
                alt={metadata.title}
                className="w-full shadow-md aspect-square"
                width={1200}
                height={800}
                priority
                sizes="(max-width: 700px) 90vw, (max-width: 1024px) 80vw, 50vw"
                placeholder="blur"
                blurDataURL="/blogImages/hero/placeholder.webp"
              />
            )}
          </div>
        </div>
        <hr />
        <div className="max-w-4xl mx-auto mt-10 px-6 lg:px-0">
          <div className="relative">
            <h3 className="text-[#f1aa4e] text-3xl font-semibold">At a Glance</h3>
            <hr className=" my-3 border-t-2 border-[#f1aa4e]" />
            <ul className="list-disc pl-5">
              {metadata.at_a_glance.map((point, index) => (
                <li key={index} className="text-gray-700 text-xl my-6 marker:text-[#f1aa4e]">
                  {point}
                </li>
              ))}
              <hr />
            </ul>
          </div>
          <div
            className="text-left max-w-4xl text-md md:text-xl mt-10 text-gray-900 prose mx-auto"
            dangerouslySetInnerHTML={createMarkup(content, metadata.quotes, metadata.metrics)}
          ></div>
          <MetaMetricRenderer metrics={metadata.metrics} />
        </div>
      </section>

      <hr className="w-10/12 mx-auto mb-12" />
      <ContactUsHome />
      {showButton && (
        <Link href="/blog/future-proof-your-hospitality-supply-chain">
          <span className="fixed bottom-5 right-5 bg-amber-600 text-white rounded-lg shadow-lg z-50 hover:translate-x-1 transition-all duration-300 inline-flex gap-2 p-3 cursor-pointer">
            {/* <DownloadIcon /> */}
            Read Full White Paper
          </span>
        </Link>
      )}
      {/* <ReportFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} /> */}
      <div id="contact-us">
        <Footer />
      </div>
    </div>
  );
}
